import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Modal,
  Paper,
  Box,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { makeStyles, useTheme } from '@mui/styles'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import RefreshIcon from '@mui/icons-material/RefreshRounded'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import Draggable from 'react-draggable'
import AiService from '@/modules/_services/ai.service'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import lang from 'lang'
import CustomScrollbar from '@/modules/base/components/custom-scrollbar/custom-scrollbar'
import ThemeInterface from '@/theme-interface'
import { useDraggableResizable } from '@/modules/base/components/draggable-resizable-modal'

const STORAGE_KEY = 'ai-text-transform-popover-size'
const DEFAULT_WIDTH = 700
const DEFAULT_HEIGHT = 600
const MIN_WIDTH = 550
const MIN_HEIGHT = 400

const loadStoredSize = (): { width: number; height: number } => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed.width && parsed.height) {
        return {
          width: Math.max(MIN_WIDTH, parsed.width),
          height: Math.max(MIN_HEIGHT, parsed.height),
        }
      }
    }
  } catch {
    // Ignore parsing errors
  }
  return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT }
}

const saveSize = (size: { width: number; height: number }) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(size))
  } catch {
    // Ignore storage errors
  }
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  modal: {
    // Make modal background transparent and don't block pointer events on background
    '& .MuiBackdrop-root': {
      backgroundColor: 'transparent',
      pointerEvents: 'none',
    },
  },
  paper: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 4,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    outline: 'none',
    pointerEvents: 'auto',
  },
  content: {
    padding: 20,
    paddingTop: 16,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: 1,
    minHeight: 0,
    gap: 16,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    flexShrink: 0,
    userSelect: 'none',
    borderBottom: theme.border,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  headerCenter: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    color: theme.textMuted,
  },
  scrollableContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  title: {
    fontWeight: 600,
    fontSize: theme.fontSizes.smedium,
  },
  closeButton: {
    padding: 4,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.text,
    marginBottom: 8,
    flexShrink: 0,
  },
  originalText: {
    padding: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderRadius: 4,
    wordWrap: 'break-word',
    flex: 1,
    minHeight: 0,
    '& p': {
      margin: '0.5em 0',
    },
    '& strong': {
      fontWeight: 600,
    },
    '& ul, & ol': {
      paddingLeft: 20,
      margin: '0.5em 0',
      listStyleType: 'disc',
    },
  },
  resultArea: {
    padding: 12,
    backgroundColor: '#fff',
    border: theme.border,
    borderRadius: 4,
    wordWrap: 'break-word',
    flex: 1,
    minHeight: 0,
    '& p': {
      margin: '0.5em 0',
    },
    '& strong': {
      fontWeight: 600,
    },
    '& ul, & ol': {
      paddingLeft: 20,
      margin: '0.5em 0',
      listStyleType: 'disc',
    },
  },
  navigationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  variantInfo: {
    fontSize: 13,
    color: 'rgba(0, 0, 0, 0.6)',
    minWidth: 80,
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    minHeight: 100,
  },
  actions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
    flexShrink: 0,
    paddingTop: 16,
  },
  leftActions: {
    display: 'flex',
    gap: 12,
  },
  rightActions: {
    display: 'flex',
    gap: 12,
  },
  button: {
    textTransform: 'none',
    borderRadius: 4,
    padding: '8px 16px',
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    minHeight: 100,
  },
  resizeHandle: {
    position: 'absolute',
    right: 3,
    bottom: 3,
    width: 20,
    height: 20,
    cursor: 'nwse-resize',
    zIndex: 10,
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 2,
      bottom: 2,
      width: 10,
      height: 10,
      borderRight: `2px solid ${theme.textMuted}`,
      borderBottom: `2px solid ${theme.textMuted}`,
    },
  },
}))

interface AiTextTransformPopoverProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  onApply: (result: string) => void
  selectedText: string
  optionLabel: string
  optionId: string
}

export const AiTextTransformPopover: React.FC<AiTextTransformPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  onApply,
  selectedText,
  optionLabel,
  optionId,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null)

  // Memoize initial size to prevent infinite loops
  const initialSize = useMemo(() => loadStoredSize(), [])

  // Use the shared draggable/resizable hook
  const {
    currentSize,
    currentPosition,
    isDragging,
    paperRef,
    handleDragStart,
    handleDragStop,
    handleResizeStart,
    bounds,
    isResizingRef,
  } = useDraggableResizable({
    size: initialSize,
    position,
    onPositionChange: setPosition,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    anchorEl,
    open,
    onResizeEnd: saveSize,
  })

  const currentResult = useMemo(
    () => suggestions[currentIndex] || '',
    [suggestions, currentIndex],
  )

  const processText = useCallback(async() => {
    setLoading(true)
    setError('')

    try {
      const processed = await AiService.transformText(selectedText, optionId)
      setSuggestions((prev) => {
        const newSuggestions = [...prev, processed]
        setCurrentIndex(newSuggestions.length - 1)
        return newSuggestions
      })
    } catch {
      setError(lang.ai.textTransform.popover.errorMessage)
    } finally {
      setLoading(false)
    }
  }, [selectedText, optionId])

  const handleApply = useCallback(() => {
    if (currentResult) {
      onApply(currentResult)
      onClose()
    }
  }, [currentResult, onApply, onClose])

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1))
  }, [])

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(suggestions.length - 1, prev + 1))
  }, [suggestions.length])

  const handleNewSuggestion = useCallback(() => {
    processText()
  }, [processText])

  useEffect(() => {
    if (open && selectedText) {
      setSuggestions([])
      setCurrentIndex(0)
      setError('')
      setPosition(null) // Reset position when reopening
      processText()
    }
  }, [open, selectedText, processText])

  // Handle click outside to close (but not during drag/resize)
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (isDragging || isResizingRef.current) return
    // Only close if clicking directly on the backdrop (modal root), not on children
    if (e.target === e.currentTarget) {
      onClose()
    }
  }, [isDragging, onClose, isResizingRef])

  const renderResultContent = () => {
    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress color="primary" size={32} />
        </div>
      )
    }

    if (error) {
      return (
        <div className={classes.errorMessage}>
          {error}
        </div>
      )
    }

    if (currentResult) {
      return (
        <CustomScrollbar className={classes.resultArea}>
          <div
            dangerouslySetInnerHTML={{ __html: currentResult }}
          />
        </CustomScrollbar>
      )
    }

    return (
      <div className={classes.resultArea}>
        <div>
          {lang.ai.textTransform.popover.placeholderText}
        </div>
      </div>
    )
  }

  const hasMultipleSuggestions = !loading && suggestions.length > 1
  const canShowNewSuggestion = !loading

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modal}
      hideBackdrop={false}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      slotProps={{
        backdrop: {
          onClick: handleBackdropClick,
        },
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
        }}
        onClick={handleBackdropClick}
      >
        <Draggable
          position={currentPosition}
          onStart={handleDragStart}
          onStop={handleDragStop}
          nodeRef={paperRef}
          handle=".ai-text-transform-drag-handle"
          bounds={bounds}
        >
          <Paper
            elevation={6}
            className={classes.paper}
            style={{
              width: currentSize.width,
              height: currentSize.height,
            }}
            ref={paperRef}
          >
            {/* Header with drag handle */}
            <Box
              className={`${classes.header} ai-text-transform-drag-handle`}
              sx={{
                cursor: isDragging ? 'grabbing' : 'grab',
              }}
            >
              <Box className={classes.headerLeft}>
                <div className={classes.title}>
                  {optionLabel}
                </div>
              </Box>

              <Box className={classes.headerCenter}>
                <Box className={classes.dragHandle}>
                  <DragIndicatorIcon fontSize="small" />
                </Box>
              </Box>

              <Box className={classes.headerRight}>
                <IconButton
                  className={classes.closeButton}
                  onClick={onClose}
                  onMouseDown={(e) => e.stopPropagation()}
                  size="small"
                >
                  <CloseIcon fontSize="small" htmlColor={theme.text} />
                </IconButton>
              </Box>
            </Box>

            <Box className={classes.content}>
              <div className={classes.scrollableContent}>
                <div className={classes.section}>
                  <div className={classes.label}>
                    {lang.ai.textTransform.popover.originalLabel}
                  </div>
                  <CustomScrollbar className={classes.originalText}>
                    <div
                      dangerouslySetInnerHTML={{ __html: selectedText }}
                    />
                  </CustomScrollbar>
                </div>

                <div className={classes.section}>
                  <div className={classes.resultHeader}>
                    <div className={classes.label}>
                      {lang.ai.textTransform.popover.resultLabel}
                    </div>
                  </div>

                  {renderResultContent()}
                </div>
              </div>

              <div className={classes.actions}>
                <div className={classes.leftActions}>
                  {/* Navigate through suggestions */}
                  {hasMultipleSuggestions && (
                    <div className={classes.navigationBar}>
                      <IconButton
                        size="small"
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}
                      >
                        <ArrowBackIcon />
                      </IconButton>
                      <div className={classes.variantInfo}>
                        {lang.ai.textTransform.popover.variantLabel(currentIndex + 1, suggestions.length)}
                      </div>
                      <IconButton
                        size="small"
                        onClick={handleNext}
                        disabled={currentIndex === suggestions.length - 1}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </div>
                  )}
                </div>
                <div className={classes.rightActions}>
                  {canShowNewSuggestion && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleNewSuggestion}
                      startIcon={<RefreshIcon />}
                      disabled={loading}
                      className={classes.button}
                    >
                      {lang.ai.textTransform.popover.buttons.newSuggestion}
                    </Button>
                  )}
                  <ButtonIconSquare
                    icon={CheckIcon}
                    onClick={handleApply}
                    disabled={loading || !currentResult}
                    iconWidth="14px"
                    label={lang.ai.textTransform.popover.buttons.apply}
                    dataQsId="ai-text-transform-apply"
                  />
                </div>
              </div>
            </Box>

            {/* Resize Handle */}
            <Box
              className={classes.resizeHandle}
              onMouseDown={handleResizeStart}
            />
          </Paper>
        </Draggable>
      </Box>
    </Modal>
  )
}
